import { themeService } from '../../services/ThemeService';
import type { ColorValue, Size } from '../types';

// Re-export from core
export * from '@supply-mover-2-0/core';

// Create default color rule - returns 'default' type which evaluates to null (uses textPrimary)
export function createDefaultColorRule() {
	return {
		type: 'default' as const,
	};
}

export interface SizeStyleConfig {
	fontSize: string;
	padding: string;
	iconSize: number;
}

// Size mappings for badges, pills, and text. 'xxs' and 'xl' have no dedicated
// entry; callers fall back to md.
export const SIZE_MAP: Record<'xs' | 'sm' | 'md' | 'lg', SizeStyleConfig> &
	Partial<Record<Size, SizeStyleConfig>> = {
	xs: {
		fontSize: '0.625rem',
		padding: '0.0625rem 0.375rem',
		iconSize: 10,
	},
	sm: {
		fontSize: '0.75rem',
		padding: '0.125rem 0.5rem',
		iconSize: 12,
	},
	md: {
		fontSize: '0.875rem',
		padding: '0.25rem 0.75rem',
		iconSize: 14,
	},
	lg: {
		fontSize: '1rem',
		padding: '0.375rem 1rem',
		iconSize: 16,
	},
};

// Apply color rules to style
export function applyColorToStyle(
	style: any,
	backgroundColor?: string,
	textColor?: string,
	borderColor?: string,
): any {
	return {
		...style,
		...(backgroundColor && { backgroundColor }),
		...(textColor && { color: textColor }),
		...(borderColor && { borderColor }),
	};
}

/**
 * Evaluates a conditional color rule against a data object
 * @param ruleCondition The conditional color rule to evaluate
 * @param data The data object containing field values
 * @returns true if the condition matches, false otherwise
 */
function evaluateColorCondition(
	ruleCondition: any,
	data: Record<string, any>,
): boolean {
	const fieldName = ruleCondition.field?.fieldName;
	const fieldValue = data[fieldName];

	if (fieldValue == null) {
		return false;
	}

	const value = ruleCondition.value;
	const ignoreCase = ruleCondition.ignoreCase === true;

	// Helper to compare values with optional case insensitivity
	const compareValues = (a: unknown, b: unknown): boolean => {
		if (ignoreCase && typeof a === 'string' && typeof b === 'string') {
			return a.toLowerCase() === b.toLowerCase();
		}
		return a === b;
	};

	switch (ruleCondition.operator) {
		case 'equals':
			return compareValues(fieldValue, value);

		case 'not_equals':
			return !compareValues(fieldValue, value);

		case 'in':
			if (Array.isArray(value)) {
				return value.some((v) => compareValues(fieldValue, v));
			}

			return compareValues(fieldValue, value);

		case 'not_in':
			if (Array.isArray(value)) {
				return !value.some((v) => compareValues(fieldValue, v));
			}

			return !compareValues(fieldValue, value);

		default:
			return false;
	}
}

/**
 * Coerce a resolved cell value into a string lookup key. Naive String(value)
 * would turn null/undefined/NaN/objects into 'null'/'undefined'/'NaN'/
 * '[object Object]' and accidentally match literal map keys with those
 * spellings; returning null signals "no match possible".
 */
function coerceLookupKey(value: unknown): string | null {
	if (typeof value === 'string') {
		return value;
	}
	if (typeof value === 'number' && Number.isFinite(value)) {
		return String(value);
	}
	if (typeof value === 'boolean' || typeof value === 'bigint') {
		return String(value);
	}
	return null;
}

/**
 * Match a resolved cell value against a config entry's `value`. Strict
 * equality first, then string-coerced equality so numeric/boolean cell
 * values match their authored string spellings (mirrors valueMappingEngine).
 */
function matchesEntryValue(resolved: unknown, entryValue: unknown): boolean {
	if (resolved === entryValue) {
		return true;
	}
	const resolvedKey = coerceLookupKey(resolved);
	const entryKey = coerceLookupKey(entryValue);
	return resolvedKey != null && entryKey != null && resolvedKey === entryKey;
}

// Semantic color names authored by status_map / status / value-mapping rules
// (e.g. { type: 'warning' }), resolved against the active theme's semantic
// palette. 'error' is an authored alias for danger; 'default' renders as a
// neutral (muted) chip.
const SEMANTIC_TYPE_THEME_KEYS: Record<
	string,
	'success' | 'warning' | 'danger' | 'info' | 'muted' | 'primary' | 'secondary'
> = {
	success: 'success',
	warning: 'warning',
	danger: 'danger',
	error: 'danger',
	info: 'info',
	muted: 'muted',
	default: 'muted',
	primary: 'primary',
	secondary: 'secondary',
};

/**
 * Resolve a ColorValue that may carry only a semantic `type` (no explicit
 * colors) into concrete colors. Explicit bg/text/border always win; semantic
 * types render as a solid fill + white text, matching the authored hex
 * convention used by e.g. the transaction status mappings.
 */
function resolveSemanticColor(
	color: ColorValue | null | undefined,
): ColorValue | null {
	if (color == null) {
		return null;
	}
	if (color.bg != null || color.text != null || color.border != null) {
		return color;
	}
	const semanticType = color.type;
	if (
		semanticType == null ||
		!Object.prototype.hasOwnProperty.call(
			SEMANTIC_TYPE_THEME_KEYS,
			semanticType,
		)
	) {
		return color;
	}
	const themeKey = SEMANTIC_TYPE_THEME_KEYS[semanticType];
	if (themeKey == null) {
		return color;
	}
	return {
		bg: themeService.getColors().values[themeKey],
		text: '#ffffff',
		opacity: color.opacity,
	};
}

/**
 * Evaluate color rules with optional data for conditional rules
 * @param rule The color rule configuration
 * @param data Optional data object for evaluating conditional rules
 * @returns ColorValue or null
 */
export function evaluateColorRule(
	rule: any,
	data?: Record<string, any>,
): ColorValue | null {
	if (!rule) {
		return null;
	}

	// Handle dynamic color rules - color comes from a field value
	if (rule.type === 'dynamic' && rule.colorField && data) {
		const colorValue = getNestedFieldValue(data, rule.colorField);
		if (colorValue) {
			return {
				bg: undefined,
				text: colorValue,
			};
		}

		return null;
	}

	// Handle field-based color rules - background color comes from a field value
	// Used for badges/chips where the background color is stored in the data
	if (rule.type === 'field' && rule.fieldName && data) {
		const colorValue = getNestedFieldValue(data, rule.fieldName);
		if (colorValue) {
			return {
				bg: colorValue,
				text: '#ffffff',
			};
		}

		return null;
	}

	// Handle conditional color rules
	if (rule.type === 'conditional' && rule.rules && data != null) {
		for (const ruleCondition of rule.rules) {
			if (evaluateColorCondition(ruleCondition, data)) {
				return ruleCondition.color ?? null;
			}
		}

		// Return default color if no condition matches
		if (rule.defaultColor != null) {
			return rule.defaultColor;
		}

		return null;
	}

	// Handle static color rules
	if (rule.type === 'static' && rule.staticColor) {
		return {
			bg: rule.staticColor.bg,
			text: rule.staticColor.text,
		};
	}

	// Handle theme color rules - references theme color keys
	if (rule.type === 'theme' && rule.themeColor) {
		const themeColors = themeService.getColors();
		const colorKey: string = rule.themeColor;
		// Config themeColor keys are free-form strings; unknown keys resolve
		// to undefined (no color applied).
		const themeValues: Record<string, string | undefined> = themeColors.values;

		// Check if it's a text color key
		if (colorKey.startsWith('text')) {
			return {
				bg: undefined,
				text: themeValues[colorKey],
			};
		}

		// Check if it's a background color key
		if (
			colorKey.startsWith('bg') ||
			colorKey === 'background' ||
			colorKey === 'surface'
		) {
			return {
				bg: themeValues[colorKey],
				text: undefined,
			};
		}

		// For other color keys, use as text color
		return {
			bg: undefined,
			text: themeValues[colorKey],
		};
	}

	// Handle range-mapping color rules — match numeric value to a range
	if (rule.type === 'range-mapping' && rule.rangeMapping && data != null) {
		// The value to check comes from the field's data source, passed via
		// displayHelpers which resolves it before calling evaluateColorRule.
		// We receive the raw data object, so we need the resolved numeric value.
		// displayHelpers passes `data` which is the full row — the caller should
		// also pass the resolved value. For now, check `data.__resolvedValue`
		// which displayHelpers sets, or fall back to checking all ranges.
		const numValue =
			typeof data['__resolvedValue'] === 'number'
				? data['__resolvedValue']
				: typeof data['__resolvedValue'] === 'string'
					? parseFloat(data['__resolvedValue'])
					: null;

		if (numValue != null && !isNaN(numValue)) {
			for (const range of rule.rangeMapping) {
				const min =
					typeof range.min === 'string' ? parseFloat(range.min) : range.min;
				const max =
					typeof range.max === 'string' ? parseFloat(range.max) : range.max;
				if (!isNaN(min) && !isNaN(max) && numValue >= min && numValue <= max) {
					return range.color ?? null;
				}
			}
		}

		return null;
	}

	// status_map: look up the cell's resolved value in a value→color map.
	// Authored as { type: 'status_map', statusMap: { active: { type: 'warning' } } }.
	if (rule.type === 'status_map' && rule.statusMap != null && data != null) {
		const key = coerceLookupKey(data['__resolvedValue']);
		if (
			key != null &&
			Object.prototype.hasOwnProperty.call(rule.statusMap, key)
		) {
			return resolveSemanticColor(rule.statusMap[key]);
		}
		return null;
	}

	// status: ordered entry list matched against the cell's resolved value.
	// Authored as { type: 'status', rules: [{ value: 'sent', color: { type: 'success' } }] }.
	if (rule.type === 'status' && Array.isArray(rule.rules) && data != null) {
		const resolved = data['__resolvedValue'];
		for (const entry of rule.rules) {
			if (matchesEntryValue(resolved, entry.value)) {
				return resolveSemanticColor(entry.color);
			}
		}
		if (rule.defaultColor != null) {
			return resolveSemanticColor(rule.defaultColor);
		}
		return null;
	}

	// value_mapping / value-mapping with entries on the rule itself. The bare
	// { type: 'value-mapping' } form (entries on the sibling
	// styleConfig.valueMapping) is resolved upstream by displayHelpers via the
	// mapping result's color, so returning null here is correct for it.
	if (rule.type === 'value-mapping' || rule.type === 'value_mapping') {
		if (Array.isArray(rule.valueMapping) && data != null) {
			const resolved = data['__resolvedValue'];
			for (const entry of rule.valueMapping) {
				if (matchesEntryValue(resolved, entry.value)) {
					return resolveSemanticColor(entry.color);
				}
			}
		}
		return null;
	}

	// boolean_map: color by the cell's boolean value ('true'/'false' string
	// spellings included, mirroring valueMappingEngine's boolean handling).
	if (rule.type === 'boolean_map' && rule.booleanMap != null && data != null) {
		const resolved = data['__resolvedValue'];
		const boolKey =
			typeof resolved === 'boolean'
				? resolved
				: resolved === 'true'
					? true
					: resolved === 'false'
						? false
						: null;
		if (boolKey != null) {
			return resolveSemanticColor(
				boolKey ? rule.booleanMap.true : rule.booleanMap.false,
			);
		}
		return null;
	}

	// Handle default type
	if (rule.type === 'default') {
		return null; // Use default theme colors
	}

	// Fallback for old format
	return {
		bg: rule?.backgroundColor,
		text: rule?.textColor,
	};
}

// Helper to get nested field value
function getNestedFieldValue(data: any, fieldPath: string): any {
	if (!data || !fieldPath) {
		return null;
	}

	const keys = fieldPath.split('.');
	let value = data;

	for (const key of keys) {
		if (value == null) {
			return null;
		}
		value = value[key];
	}

	return value;
}
